import type { NextPage } from "next";
import Image from "next/legacy/image";
import {
  GiftIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  HeartIcon,
  HandRaisedIcon,
} from "@heroicons/react/24/outline";
import Page from "@/components/Page";

import directBanner from "../public/images/merchant-donation.jpg";
import CheckoutForm from "@/components/CheckoutForm";
import Banner from "@/components/Banner";
import { SocialIcon } from "react-social-icons";
import Modal from "@/components/Dialog";
import { useRouter } from "next/router";
import Thanks from "@/components/Thanks";
import Link from "next/link";

const SupportUs: NextPage = () => {
  const router = useRouter();

  return (
    <Page title="Support Us - The Cake Detective">
      <Page.Header />
      <Banner banner={directBanner} aspectH="aspect-h-4" opacity="opacity-50">
        <div className="container mx-auto py-8 px-4 sm:py-16 sm:px-6 lg:px-8 text-right">
          <h2 className="block text-xs sm:text-base uppercase text-brand-teal font-semibold tracking-wider">
            The Cake Detective
          </h2>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight text-cream lowercase">
            Support our mission
          </h1>
        </div>
      </Banner>
      <Page.Content>
        <div className="pt-4 md:pt-12">
          <div className="relative bg-white/25 rounded-xl">
            <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
              <div className="px-4 py-4 md:py-8 max-w-xl mx-auto sm:px-6 lg:py-16 lg:max-w-none lg:mx-0 lg:px-0 flex items-center">
                <div>
                  <div className="flex flex-row uppercase text-brand-ranga space-x-2 items-center justify-start font-semibold tracking-wider">
                    <span>Gift a cake</span>
                    <GiftIcon
                      className="h-4 w-4 text-brand-ranga"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-1">
                    <h2 className="text-3xl font-extrabold tracking-tight text-brand-navy">
                      Direct Donations
                    </h2>
                    <p className="mt-4 text-lg text-gray-500">
                      For a direct one off donation to support The Cake
                      Detective please click on the button and follow the
                      prompts for a secure payment.
                    </p>
                    <p className="mt-4 text-lg text-gray-500">
                      100% of all donations go towards ingredients and supplies
                      for donated cakes.
                    </p>
                  </div>
                  <div className="mt-8 border-t border-gray-200 pt-6">
                    <blockquote>
                      <div>
                        <p className="text-base text-gray-500">
                          The Cake Detective is a registered charity
                          (registration number CC57514) and all donations over
                          $5 are tax deductible.
                        </p>
                      </div>
                    </blockquote>
                  </div>
                </div>
              </div>
              <div className="mt-12 sm:mt-16 lg:mt-0">
                <div className="px-8 lg:px-0 lg:m-0 lg:relative lg:h-full max-x-xl">
                  <CheckoutForm />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 bg-white/25 rounded-xl p-4 md:p-6">
            <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
              <div className="px-4 max-w-xl mx-auto sm:px-6 lg:py-24 lg:max-w-none lg:mx-0 lg:px-0 lg:col-start-2">
                <div>
                  <div className="flex flex-row uppercase text-brand-ranga space-x-2 items-center justify-start font-semibold tracking-wider">
                    <span>Become a deputy</span>
                    <ShieldCheckIcon
                      className="h-4 w-4 text-brand-ranga"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-1">
                    <h2 className="text-3xl font-extrabold tracking-tight text-gray-900">
                      Regular donations
                    </h2>
                    <p className="mt-4 text-lg text-gray-500">
                      Become a deputy of The Cake Dective by donating a small
                      amount on an ongoing basis. You can choose a schedule that
                      suits you, e.g., weekly, monthly, etc.
                    </p>
                    <p className="mt-4 text-lg text-gray-500">
                      Simply set up a regular automatic payment through internet
                      banking.
                    </p>

                    <div className="mx-6 my-12">
                      <div className="overflow-hidden rounded-lg bg-brand-navy px-4 py-5 shadow-md sm:p-6">
                        <dt className="truncate text-sm font-medium text-brand-teal">
                          THE CAKE DETECTIVE CHARITABLE TRUST
                        </dt>
                        <dd className="mt-1 text-3xl font-semibold tracking-tight text-white">
                          12-3171-0031366-000
                        </dd>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200">
                  <blockquote>
                    <div>
                      <p className="text-base text-navy/75">
                        The Cake Detective is a registered charity (registration
                        number CC57514) and all donations over $5 are tax
                        deductible.
                      </p>
                    </div>
                  </blockquote>
                </div>
              </div>
              <div className="mt-12 sm:mt-16 lg:mt-0 lg:col-start-1">
                <div className="relative  pr-4 -ml-48 sm:pr-6 md:-ml-16 lg:px-0 lg:m-0 lg:relative lg:h-full flex items-center justify-center rounded-lg overflow-hidden">
                  <Image
                    src="/images/cakes/gallery/minion-clear.png"
                    className={`w-full object-cover object-center`}
                    layout="fill"
                    alt="Become a deputy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="my-4 md:my-12 bg-white/25 py-4 md:py-12 rounded-xl">
          <div className="max-w-xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <h2 className="sr-only">A better way to send money.</h2>
            <dl className="space-y-10 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8">
              <div className="bg-white/50 rounded-xl p-4">
                <dt>
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-brand-pink text-white">
                    <CurrencyDollarIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                    />
                  </div>
                  <p className="mt-5 text-lg leading-6 font-medium text-gray-900">
                    Givealittle
                  </p>
                </dt>
                <dd className="mt-2 text-base text-gray-500">
                  If you would prefer to make a donation via the Give a Little
                  page please click the button to be taken through to our page.
                </dd>
                <dd className="mt-4 text-base text-gray-500">
                  <a
                    href="https://givealittle.co.nz/org/the-cake-detective-charitable-trust#:~:text=The%20Cake%20Detective%20is%20donating,are%20having%20a%20tough%20time.&text=Lots%20of%20us%20have%20memories,infamous%20Women's%20Weekly%20cake%20book."
                    className="inline-block bg-brand-pink py-2 px-4 border border-transparent rounded-md text-base font-bold text-teal-50 hover:bg-brand-ranga-50"
                  >
                    Donate on Givealittle
                  </a>
                </dd>
              </div>
              <div className="bg-white/50 rounded-xl p-4">
                <dt>
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-brand-pink text-white">
                    <HandRaisedIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="mt-5 text-lg leading-6 font-medium text-gray-900">
                    Volunteer
                  </p>
                </dt>
                <dd className="mt-2 text-base text-gray-500">
                  If you are a skilled cake maker and would like to volunteer
                  your time and skills please get in touch.
                </dd>
                <dd className="mt-4 text-base text-gray-500">
                  <Link
                    href="/contact-us"
                    className="inline-block bg-brand-pink py-2 px-4 border border-transparent rounded-md text-base font-bold text-teal-50 hover:bg-brand-ranga-50"
                  >
                    Contact Us
                  </Link>
                </dd>
              </div>
              <div className="bg-white/50 rounded-xl p-4">
                <dt>
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-brand-pink text-white">
                    <HeartIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="mt-5 text-lg leading-6 font-medium text-gray-900">
                    Spread The Word
                  </p>
                </dt>
                <dd className="mt-2 text-base text-gray-500">
                  Spread the word on Social media by following on Instagram and
                  Facebook. Like and share away.
                </dd>
                <dd className="mt-4 text-base text-gray-500 space-x-2">
                  <SocialIcon
                    url="https://www.instagram.com/thecakedetective"
                    style={{ width: "32px", height: "32px" }}
                  />
                  <SocialIcon
                    url="https://www.facebook.com/thecakedetective/"
                    style={{ width: "32px", height: "32px" }}
                  />
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <Thanks />
        {"success" in router.query && (
          <Modal
            initialOpen
            onClose={() =>
              router.replace("/support-us", undefined, { shallow: true })
            }
            closeOnly
            closeText="Ok"
          >
            <span>Thanks for your donation!</span>
          </Modal>
        )}
      </Page.Content>
      <Page.Footer />
    </Page>
  );
};

export default SupportUs;
